import { useId, type ComponentProps } from 'react'

import { Button, Group, Input, Label, NumberField } from 'react-aria-components'
import type { Control, FieldValues, UseControllerProps } from 'react-hook-form'

import { Icon } from '@/components/icon'
import { Render } from '@/components/render-if'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { cn } from '@/infra'

interface ICounterInputProps extends ComponentProps<typeof NumberField> {
  label?: string
  invalid?: boolean
  suffixSingular?: string
  suffixPlural?: string
}

export const CounterInput = ({
  label,
  invalid,
  value: fieldValue,
  defaultValue,
  suffixSingular,
  suffixPlural,
  ...props
}: ICounterInputProps) => {
  const id = useId()
  const value = fieldValue ?? defaultValue
  const numericValue = typeof value === 'number' ? value : 0
  const getSuffix = () => {
    if (!suffixSingular || !suffixPlural) return null
    return numericValue === 1 ? suffixSingular : suffixPlural
  }
  const suffix = getSuffix()

  const getSuffixColor = () => {
    if (props.isDisabled) return 'text-neutral-500'
    if (value) return 'text-neutral-800'
    return undefined
  }

  return (
    <NumberField defaultValue={defaultValue} value={value} minValue={1} className="flex flex-col gap-2" {...props}>
      <Render.If condition={label}>
        {(_label) => (
          <Label id={id} className={cn('font-nunito data-[error=true]:text-destructive form-label')}>
            {_label}
          </Label>
        )}
      </Render.If>
      <div className="*:not-first:mt-2">
        <Group
          aria-invalid={invalid}
          className={cn(
            'data-focus-within:border-ring data-focus-within:ring-ring/20 aria-invalid:border-destructive relative inline-flex h-11 w-full items-center overflow-hidden rounded-lg border text-sm whitespace-nowrap transition-shadow data-focus-within:ring-[3px] data-focus-within:outline-none',
            value ? 'border-input bg-slate-100 text-neutral-800' : undefined,
            'aria-disabled:bg-neutral-100 aria-disabled:text-neutral-500',
          )}
        >
          <Button
            slot="decrement"
            className="border-input text-muted-foreground/70 hover:bg-accent hover:text-accent-foreground -ms-px flex aspect-square h-[inherit] items-center justify-center rounded-s-lg border bg-white text-base transition-shadow disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Icon name="minus" />
          </Button>
          <label
            className={cn(
              'flex flex-1 items-center justify-center gap-1',
              suffix && !props.isDisabled ? 'cursor-text' : undefined,
            )}
          >
            <Input
              aria-labelledby={id}
              data-testid="counter-input"
              className={cn(
                'aria-disabled:bg-neutral-100 aria-disabled:text-neutral-500',
                value ? 'bg-slate-100 text-neutral-800' : undefined,
                'py-2 text-base tabular-nums placeholder:text-neutral-500 focus:outline-none',
                suffix ? 'field-sizing-content min-w-[1ch] shrink-0 px-0 text-right' : 'w-full grow px-3 text-center',
              )}
            />
            <Render.If condition={suffix}>
              {(_suffix) => (
                <span
                  data-testid="counter-input-suffix"
                  className={cn('pointer-events-none shrink-0 pl-1 text-base font-normal', getSuffixColor())}
                >
                  {_suffix}
                </span>
              )}
            </Render.If>
          </label>
          <Button
            slot="increment"
            className="border-input text-muted-foreground/70 hover:bg-accent hover:text-accent-foreground -me-px flex aspect-square h-[inherit] items-center justify-center rounded-e-lg border bg-white text-base transition-shadow disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Icon name="plus" />
          </Button>
        </Group>
      </div>
    </NumberField>
  )
}

type ICounterInputFieldProps<
  TFieldValues extends FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
> = Omit<UseControllerProps<TFieldValues>, 'control'> &
  ICounterInputProps & {
    control?: Control<TFieldValues, TContext, TTransformedValues>
    errorSize?: 'sm' | 'md' | 'lg'
  }
export const CounterInputField = <
  TFieldValues extends FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>({
  control,
  label,
  errorSize,
  ...props
}: ICounterInputFieldProps<TFieldValues, TContext, TTransformedValues>) => {
  const id = useId()
  return (
    <FormField
      control={control}
      name={props.name}
      render={({ field, fieldState: { invalid } }) => (
        <FormItem>
          <FormLabel id={id} aria-disabled={props.disabled}>
            {label}
          </FormLabel>
          <FormControl>
            <CounterInput
              {...field}
              {...props}
              aria-label={label}
              invalid={invalid}
              isDisabled={props.disabled}
              aria-labelledby={id}
              onChange={(v) => {
                if (Number.isNaN(v)) {
                  field.onChange(props.minValue ?? 0)
                  props.onChange?.(props.minValue ?? 0)
                  return
                }
                field.onChange(v)
                props.onChange?.(v)
              }}
            />
          </FormControl>
          <FormMessage size={errorSize} />
        </FormItem>
      )}
    />
  )
}
